class ProductGallery extends HTMLElement {
  constructor() {
    super();
    this.imageContainers = [];
    this.images = [];
    this.thumbnailContainer = null;
  }

  connectedCallback() {
    // Use a small timeout to ensure DOM is ready,
    // especially if children are added dynamically or if not using Shadow DOM.
    setTimeout(() => this.init(), 0);
  }

  init() {
    this.imageContainers = Array.from(
      this.querySelectorAll(".image-container"),
    );
    this.images = this.imageContainers.map((container) =>
      container.querySelector("img"),
    );

    if (this.images.length > 1) {
      this.createThumbnails();
      this.setupZoom();
      this.setupActiveThumbnailObserver();
    }
  }

  createThumbnails() {
    this.thumbnailContainer = document.createElement("div");
    this.thumbnailContainer.className = "thumbnails-picker";

    this.images.forEach((img, index) => {
      const thumb = document.createElement("div");
      thumb.className = "thumbnail";
      if (index === 0) thumb.classList.add("active");

      const thumbImg = document.createElement("img");
      thumbImg.src = img.src;
      thumb.appendChild(thumbImg);

      thumb.addEventListener("click", () => {
        this.scrollToIndex(index);
      });

      this.thumbnailContainer.appendChild(thumb);
    });

    this.appendChild(this.thumbnailContainer);
  }

  setupZoom() {
    this.imageContainers.forEach((container) => {
      const img = container.querySelector("img");
      if (!img) return;

      container.addEventListener("click", (e) => {
        if (window.innerWidth < 1024) return;

        const isCurrentlyZoomed = container.classList.contains("is-zoomed");

        // Reset zoom state of all other containers
        this.imageContainers.forEach((otherContainer) => {
          if (otherContainer !== container) {
            otherContainer.classList.remove("is-zoomed");
            this.removeZoomLens(otherContainer);
          }
        });

        if (isCurrentlyZoomed) {
          container.classList.remove("is-zoomed");
          this.removeZoomLens(container);
        } else {
          container.classList.add("is-zoomed");
          this.createZoomLens(container, img);
          this.updateZoom(e, container);
        }
      });

      container.addEventListener("mousemove", (e) => {
        if (window.innerWidth < 1024) return;
        if (!container.classList.contains("is-zoomed")) return;
        this.updateZoom(e, container);
      });

      container.addEventListener("mouseleave", () => {
        container.classList.remove("is-zoomed");
        this.removeZoomLens(container);
      });
    });
  }

  createZoomLens(container, img) {
    const lens = document.createElement("div");
    lens.className = "zoom-lens";
    lens.style.backgroundImage = `url(${img.src})`;
    // Set background size to something large for zoom effect
    lens.style.backgroundSize = `${container.offsetWidth * 2}px ${container.offsetHeight * 2}px`;
    container.appendChild(lens);
  }

  updateZoom(e, container) {
    const lens = container.querySelector(".zoom-lens");
    if (!lens) return;

    const rect = container.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Calculate background position
    const xPercent = (x / rect.width) * 100;
    const yPercent = (y / rect.height) * 100;

    lens.style.backgroundPosition = `${xPercent}% ${yPercent}%`;

    // Move lens with cursor
    const lensWidth = lens.offsetWidth;
    const lensHeight = lens.offsetHeight;

    let left = x - lensWidth / 2;
    let top = y - lensHeight / 2;

    // Keep lens within container bounds
    left = Math.max(0, Math.min(left, rect.width - lensWidth));
    top = Math.max(0, Math.min(top, rect.height - lensHeight));

    lens.style.left = `${left}px`;
    lens.style.top = `${top}px`;
  }

  removeZoomLens(container) {
    const lens = container.querySelector(".zoom-lens");
    if (lens) lens.remove();
  }

  setupActiveThumbnailObserver() {
    const options = {
      root: null, // Use the viewport as the root
      threshold: 0.5,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const index = this.imageContainers.indexOf(entry.target);
          this.updateActiveThumbnail(index);
        }
      });
    }, options);

    this.imageContainers.forEach((container) => observer.observe(container));
  }

  updateActiveThumbnail(index) {
    if (!this.thumbnailContainer) return;
    const thumbs = this.thumbnailContainer.querySelectorAll(".thumbnail");
    thumbs.forEach((thumb, i) => {
      thumb.classList.toggle("active", i === index);
    });
  }

  cleanFilename(url) {
    if (!url) return "";
    const withoutQuery = url.split("?")[0].split("#")[0];
    const filename = withoutQuery.substring(withoutQuery.lastIndexOf("/") + 1);
    return filename
      .replace(
        /(_\d+x\d*|_\d*x\d+|_pico|_icon|_thumb|_small|_compact|_medium|_large|_grande|_1024x1024|_2048x2048|_master)(\.[a-zA-Z0-9]+)?$/,
        "",
      )
      .replace(/\.[a-zA-Z0-9]+$/, "")
      .toLowerCase();
  }

  scrollToIndex(index) {
    if (index < 0 || index >= this.imageContainers.length) return;
    const targetContainer = this.imageContainers[index];
    if (!targetContainer) return;

    const isMobileCarousel =
      window.innerWidth < 768 || this.scrollWidth > this.clientWidth;

    if (isMobileCarousel) {
      this.scrollTo({
        left: targetContainer.offsetLeft,
        behavior: "smooth",
      });
    } else {
      targetContainer.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }

    this.updateActiveThumbnail(index);
  }

  scrollToVariantImage(featuredImage) {
    if (!featuredImage) return false;
    if (!this.imageContainers || this.imageContainers.length === 0) {
      this.init();
    }

    let imageId = null;
    let cleanVariantSrc = "";

    if (typeof featuredImage === "string") {
      cleanVariantSrc = this.cleanFilename(featuredImage);
    } else if (typeof featuredImage === "number") {
      imageId = String(featuredImage);
    } else if (typeof featuredImage === "object") {
      if (featuredImage.id) imageId = String(featuredImage.id);
      const src =
        featuredImage.src ||
        (featuredImage.preview_image && featuredImage.preview_image.src) ||
        "";
      if (src) cleanVariantSrc = this.cleanFilename(src);
    }

    let targetIndex = -1;

    if (imageId) {
      targetIndex = this.imageContainers.findIndex(
        (c) => c.dataset.imageId === imageId,
      );
    }

    if (targetIndex === -1 && cleanVariantSrc) {
      targetIndex = this.imageContainers.findIndex((c) => {
        const cSrc = c.dataset.imageSrc || "";
        if (cSrc && this.cleanFilename(cSrc) === cleanVariantSrc) return true;
        const img = c.querySelector("img");
        if (!img) return false;
        if (this.cleanFilename(img.src) === cleanVariantSrc) return true;
        if (img.srcset && img.srcset.includes(cleanVariantSrc)) return true;
        return false;
      });
    }

    if (targetIndex !== -1) {
      this.scrollToIndex(targetIndex);
      return true;
    } else if (
      typeof product !== "undefined" &&
      product &&
      product.images &&
      product.images.length > 0
    ) {
      // If variant image is the first product image (which may be skipped by offset: 1), scroll to the start (index 0)
      const firstImg = product.images[0];
      const isFirstImg =
        (imageId && String(firstImg.id) === imageId) ||
        (cleanVariantSrc &&
          this.cleanFilename(
            typeof firstImg === "string" ? firstImg : firstImg.src,
          ) === cleanVariantSrc);
      if (isFirstImg && this.imageContainers.length > 0) {
        this.scrollToIndex(0);
        return true;
      }
    }

    return false;
  }
}

customElements.define("product-gallery", ProductGallery);
